'use client';

import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { api, setAuthTokens, getAccessToken, getRefreshToken } from './api';
import { ensureAppPush, disableAppPush } from './fcm';
import { useAppStore } from '@/store/app';
import type { PublicUser, AuthTokens } from '@glintfans/shared';

/** Only accept same-site paths as login redirect targets (never external URLs). */
function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  if (raw.includes('://')) return null;
  return raw;
}

export function useAuth() {
  const router = useRouter();
  const { user, setUser, setAuthState } = useAppStore();

  // Read returnTo from window.location on the client instead of useSearchParams():
  // useSearchParams() suspends inside Suspense on statically-rendered pages and
  // can leave the fallback mounted forever (observed on /profile). window.location
  // is always available after hydration.
  const returnTo = safeReturnTo(
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('returnTo') : null,
  );

  const afterAuth = useCallback(() => {
    // Full-page navigation after auth: avoids client-side route-swap exceptions
    // right after the auth store flips, and guarantees the target page mounts
    // with the fresh user state (refreshing restored the page, so mount fresh).
    const dest = returnTo && returnTo !== '/login' && returnTo !== '/register' ? returnTo : '/markets';
    if (typeof window !== 'undefined') {
      window.location.assign(dest);
    }
  }, [returnTo]);

  // Restore session: fetch /users/me if a token exists
  useEffect(() => {
    if (!getAccessToken()) return;
    api
      .get<PublicUser>('/users/me')
      .then((u) => {
        setUser(u);
        setAuthState('authenticated');
        void ensureAppPush();
      })
      .catch(() => {
        setAuthTokens(null);
        setUser(null);
        setAuthState('guest');
      });
  }, [setUser, setAuthState]);

  const login = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const res = await api.post<{ user: PublicUser; tokens: AuthTokens }>('/auth/login', {
        email,
        password,
      });
      setAuthTokens(res.tokens);
      setUser(res.user);
      setAuthState('authenticated');
      void ensureAppPush();
      return res.user;
    },
    onSuccess: afterAuth,
  });

  const register = useMutation({
    mutationFn: async ({
      email,
      password,
      displayName,
      code,
    }: {
      email: string;
      password: string;
      displayName?: string;
      code: string;
    }) => {
      const res = await api.post<{ user: PublicUser; tokens: AuthTokens }>('/auth/register', {
        email,
        password,
        displayName,
        code,
      });
      setAuthTokens(res.tokens);
      setUser(res.user);
      setAuthState('authenticated');
      return res.user;
    },
    onSuccess: afterAuth,
  });

  const sendCode = useMutation({
    mutationFn: ({ email, purpose }: { email: string; purpose: 'register' | 'verify-old' | 'verify-new' }) =>
      api.post<{ sent: boolean; retryAfter: number }>('/auth/send-code', {
        email,
        purpose,
        userId: purpose === 'register' ? undefined : useAppStore.getState().user?.id,
        language: useAppStore.getState().language,
      }),
  });

  const changeEmail = useMutation({
    mutationFn: async ({
      newEmail,
      newEmailCode,
      oldEmailCode,
    }: {
      newEmail: string;
      newEmailCode: string;
      oldEmailCode: string;
    }) => {
      const res = await api.post<{ user: PublicUser; tokens: AuthTokens }>('/auth/change-email', {
        newEmail,
        newEmailCode,
        oldEmailCode,
      });
      setAuthTokens(res.tokens);
      setUser(res.user);
      return res.user;
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const user = await api.upload<PublicUser>('/users/me/avatar', form);
      setUser(user);
      return user;
    },
  });

  const logout = useCallback(async () => {
    await disableAppPush();
    const refresh = getRefreshToken();
    if (refresh) {
      try {
        await api.post('/auth/logout', { refreshToken: refresh });
      } catch {
        /* ignore */
      }
    }
    setAuthTokens(null);
    setUser(null);
    router.push('/markets');
  }, [router, setUser]);

  return { user, login, register, logout, sendCode, changeEmail, uploadAvatar };
}
